import {
  countAssessmentReportFindings,
  getAssessmentReportDomainLabel,
  getAssessmentReportFindingStatusLabel,
  getAssessmentReportPeriod,
  type AssessmentReportEditorForm,
  type AssessmentReportSourceSnapshotV2,
} from './assessmentReportDomain';
import type { AssessmentReportPurposeType } from './assessmentReportCatalogs';

const MAX_HIGHLIGHTED_SOURCES = 3;
const MAX_SOURCES_PER_DOMAIN = 2;
const MAX_OBSERVATIONS = 4;
const MAX_INLINE_TEXT_LENGTH = 280;

export const ASSESSMENT_REPORT_CONCLUSION_COMPLETION_MARKER =
  '[Complemente esta conclusão com o diagnóstico fisioterapêutico e a correlação com a avaliação clínica.]';

export interface AssessmentReportNarrativeSuggestions {
  purposeDescription: string;
  summary: {
    professionalOverview: string;
    followUpGuidance: string | null;
    observations: string[];
  };
  technical: {
    clinicalContext: string;
    integratedAnalysis: string;
    physiotherapyConclusion: string;
    recommendations: string[];
    limitations: string | null;
  };
}

export interface ReconcileAssessmentReportSuggestionsOptions {
  /**
   * Preenche campos ainda vazios. O padrão é `true`.
   *
   * Um campo apagado na mesma transição entre `previous` e `next` é tratado
   * como edição e permanece vazio.
   */
  fillEmpty?: boolean;
  /**
   * Substitui também conteúdo editado. Deve ser usado apenas após uma ação
   * explícita do profissional, como "Restaurar sugestões".
   */
  force?: boolean;
}

export const ASSESSMENT_REPORT_LEGACY_PURPOSE_PREFIX: Record<AssessmentReportPurposeType, string> = {
  patient_feedback:
    'Apresentar de forma objetiva as avaliações realizadas e os achados mais relevantes para a devolutiva.',
  clinical_follow_up:
    'Sintetizar os resultados disponíveis para apoiar o acompanhamento clínico-funcional.',
  referral:
    'Compartilhar os procedimentos realizados e os achados relevantes para a continuidade do cuidado.',
  care_transition:
    'Registrar de forma sintética o estado avaliado para apoiar a transição de cuidado.',
  interprofessional_communication:
    'Comunicar de forma objetiva os resultados das avaliações aos profissionais envolvidos no cuidado.',
  other:
    'Sintetizar as avaliações selecionadas e seus principais achados para a finalidade informada.',
};

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, ' ').trim();

const ensureSentence = (value: string): string => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';
  return /[.!?…]$/.test(normalized) ? normalized : `${normalized}.`;
};

const shortenText = (value: string, maximumLength = MAX_INLINE_TEXT_LENGTH): string => {
  const normalized = normalizeWhitespace(value);
  if (normalized.length <= maximumLength) return normalized;

  const candidate = normalized.slice(0, maximumLength + 1);
  const lastSpace = candidate.lastIndexOf(' ');
  const breakAt = lastSpace >= Math.floor(maximumLength * 0.7) ? lastSpace : maximumLength;
  return `${candidate.slice(0, breakAt).trimEnd()}…`;
};

const joinNatural = (items: readonly string[]): string => {
  const normalized = items.map(normalizeWhitespace).filter(Boolean);
  if (normalized.length === 0) return '';
  if (normalized.length === 1) return normalized[0];
  if (normalized.length === 2) return `${normalized[0]} e ${normalized[1]}`;
  return `${normalized.slice(0, -1).join(', ')} e ${normalized[normalized.length - 1]}`;
};

const compareText = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

const sortSources = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): AssessmentReportSourceSnapshotV2[] =>
  [...sources].sort((left, right) => (
    compareText(left.snapshot.assessmentDate, right.snapshot.assessmentDate)
    || compareText(left.snapshot.testLabel, right.snapshot.testLabel)
    || compareText(left.resultId, right.resultId)
  ));

const unique = (values: readonly string[]): string[] => [...new Set(values.filter(Boolean))];

const formatEvaluationCount = (count: number): string =>
  `${count} ${count === 1 ? 'avaliação' : 'avaliações'}`;

const formatResultCount = (
  count: number,
  singular: string,
  plural: string,
): string => `${count} ${count === 1 ? singular : plural}`;

const buildStatusCountParts = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string[] => {
  const counts = countAssessmentReportFindings(sources);
  return [
    counts.withinExpected > 0
      ? formatResultCount(
          counts.withinExpected,
          'resultado dentro do esperado',
          'resultados dentro do esperado',
        )
      : '',
    counts.borderline > 0
      ? formatResultCount(
          counts.borderline,
          'resultado em faixa de atenção',
          'resultados em faixa de atenção',
        )
      : '',
    counts.altered > 0
      ? formatResultCount(counts.altered, 'resultado alterado', 'resultados alterados')
      : '',
    counts.notClassified > 0
      ? formatResultCount(
          counts.notClassified,
          'resultado sem classificação conclusiva',
          'resultados sem classificação conclusiva',
        )
      : '',
    counts.notInterpretable > 0
      ? formatResultCount(
          counts.notInterpretable,
          'resultado não interpretável',
          'resultados não interpretáveis',
        )
      : '',
  ].filter(Boolean);
};

const getSourceNarrative = (source: AssessmentReportSourceSnapshotV2): string => {
  const authoredFinding = source.finding.conciseSummary?.trim();
  if (authoredFinding) return shortenText(authoredFinding);

  const sourceInterpretation = source.snapshot.interpretationText?.trim();
  if (sourceInterpretation) return shortenText(sourceInterpretation);

  const primaryMeasure = source.snapshot.primaryMeasure.formattedValue.trim();
  const sourceClassification = source.snapshot.referenceText?.trim();
  if (primaryMeasure && sourceClassification) {
    return shortenText(
      `Resultado principal de ${primaryMeasure}; a avaliação de origem registrou a classificação “${sourceClassification}”`,
    );
  }
  if (primaryMeasure) return `Resultado principal registrado: ${primaryMeasure}`;

  return 'Resultado registrado sem interpretação textual disponível';
};

const formatSourceNarrative = (source: AssessmentReportSourceSnapshotV2): string =>
  `${source.snapshot.testLabel}: ${getSourceNarrative(source)}`;

const formatSourceNames = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
  maximum = MAX_HIGHLIGHTED_SOURCES,
): string => {
  const names = unique(sortSources(sources).map((source) => source.snapshot.testLabel));
  const visible = names.slice(0, maximum);
  const remaining = names.length - visible.length;
  return remaining > 0
    ? `${joinNatural(visible)} e ${remaining} ${remaining === 1 ? 'outra avaliação' : 'outras avaliações'}`
    : joinNatural(visible);
};

const buildPurposeDescription = (form: AssessmentReportEditorForm): string => {
  const prefix = ASSESSMENT_REPORT_LEGACY_PURPOSE_PREFIX[form.purpose.type];
  if (form.sources.length === 0) return prefix;

  const period = getAssessmentReportPeriod(form.sources);
  const scope = period
    ? `${formatEvaluationCount(form.sources.length)} realizadas no período de ${period.label}`
    : `${formatEvaluationCount(form.sources.length)} selecionadas`;
  return `${prefix} O documento considera ${scope}.`;
};

const buildProfessionalOverview = (form: AssessmentReportEditorForm): string => {
  const sources = sortSources(form.sources);
  if (sources.length === 0) {
    return 'Ainda não há avaliações selecionadas para compor esta síntese.';
  }

  const period = getAssessmentReportPeriod(sources);
  const domains = unique(
    sources.map((source) => getAssessmentReportDomainLabel(source.finding.domain)),
  );
  const scopeParts = [
    `${sources.length === 1 ? 'Foi considerada' : 'Foram consideradas'} ${formatEvaluationCount(sources.length)}`,
    period ? `no período de ${period.label}` : '',
    domains.length > 0 ? `abrangendo ${joinNatural(domains)}` : '',
  ].filter(Boolean);

  const paragraphs = [`${scopeParts.join(' ')}.`];
  const statusParts = buildStatusCountParts(sources);
  if (statusParts.length > 0) {
    paragraphs.push(`Quanto à leitura dos achados, foram registrados ${joinNatural(statusParts)}.`);
  }

  const highlighted = sources
    .filter((source) =>
      source.finding.status === 'altered'
      || source.finding.status === 'borderline')
    .slice(0, MAX_HIGHLIGHTED_SOURCES);
  if (highlighted.length > 0) {
    const remaining =
      sources.filter((source) =>
        source.finding.status === 'altered'
        || source.finding.status === 'borderline').length - highlighted.length;
    const highlightedText = highlighted.map(formatSourceNarrative).join('; ');
    paragraphs.push(
      `Entre os pontos que merecem atenção: ${highlightedText}`
      + (remaining > 0
        ? `; além de ${remaining} ${remaining === 1 ? 'outro achado' : 'outros achados'}`
        : '')
      + '.',
    );
  }

  paragraphs.push(
    'Esta síntese descreve os resultados registrados e deve ser correlacionada à anamnese, ao exame físico e à evolução clínica.',
  );
  return paragraphs.map(ensureSentence).filter(Boolean).join(' ');
};

const buildFollowUpGuidance = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string | null => {
  if (sources.length === 0) return null;

  const counts = countAssessmentReportFindings(sources);
  const guidance: string[] = [];
  if (counts.altered > 0 || counts.borderline > 0) {
    guidance.push(
      'Os resultados alterados ou em faixa de atenção devem ser correlacionados ao contexto clínico pelo profissional responsável',
    );
  } else if (counts.withinExpected > 0) {
    guidance.push(
      'Os resultados devem ser acompanhados conforme os objetivos e o plano de cuidado definidos pelo profissional responsável',
    );
  }
  if (counts.notClassified > 0 || counts.notInterpretable > 0) {
    guidance.push(
      'Os resultados sem conclusão classificatória precisam ser revisados antes de orientar condutas específicas',
    );
  }
  if (guidance.length === 0) {
    guidance.push(
      'Os resultados devem ser interpretados em conjunto com a avaliação clínica pelo profissional responsável',
    );
  }
  return guidance.map(ensureSentence).join(' ');
};

const buildObservations = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string[] => {
  const sorted = sortSources(sources);
  const observations: string[] = [];

  sorted
    .filter((source) => source.validity.status !== 'valid')
    .slice(0, MAX_OBSERVATIONS)
    .forEach((source) => {
      const reasons = unique([
        source.validity.note?.trim() || '',
        ...source.validity.limitations.map((item) => item.trim()),
      ]);
      const validityLabel = source.validity.status === 'limited'
        ? 'foi considerada válida com ressalvas'
        : source.validity.status === 'invalid'
          ? 'foi considerada inválida'
          : 'não teve a validade determinada';
      observations.push(
        ensureSentence(
          `${source.snapshot.testLabel} ${validityLabel}`
          + (reasons.length > 0
            ? `: ${joinNatural(reasons.map((reason) => shortenText(reason)))}`
            : ''),
        ),
      );
    });

  const unresolved = sorted.filter((source) =>
    source.finding.status === 'not_classified'
    || source.finding.status === 'not_interpretable');
  if (unresolved.length > 0 && observations.length < MAX_OBSERVATIONS) {
    observations.push(
      ensureSentence(
        `${formatSourceNames(unresolved)} ${
          unresolved.length === 1 ? 'permanece' : 'permanecem'
        } sem conclusão classificatória`,
      ),
    );
  }

  const missingReference = sorted.filter((source) =>
    (
      source.finding.status === 'within_expected'
      || source.finding.status === 'borderline'
      || source.finding.status === 'altered'
    )
    && !source.finding.referenceLabel?.trim());
  if (missingReference.length > 0 && observations.length < MAX_OBSERVATIONS) {
    observations.push(
      ensureSentence(
        `A referência adotada em ${formatSourceNames(missingReference)} precisa ser confirmada antes da emissão`,
      ),
    );
  }

  return unique(observations).slice(0, MAX_OBSERVATIONS);
};

const buildTechnicalClinicalContext = (form: AssessmentReportEditorForm): string => {
  const sources = sortSources(form.sources);
  if (sources.length === 0) {
    return 'O contexto técnico será delimitado após a seleção das avaliações-fonte.';
  }

  const period = getAssessmentReportPeriod(sources);
  const domains = unique(
    sources.map((source) => getAssessmentReportDomainLabel(source.finding.domain)),
  );
  const periodText = period ? ` realizadas no período de ${period.label}` : '';
  const domainText = domains.length > 0 ? `, com foco em ${joinNatural(domains)}` : '';
  return ensureSentence(
    `Foram integradas ${formatEvaluationCount(sources.length)}${periodText}${domainText}. `
    + 'O contexto apresentado está restrito aos dados documentados nas avaliações-fonte e à finalidade informada para este relatório',
  );
};

const buildDomainAnalysis = (
  domainLabel: string,
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string => {
  const visible = sortSources(sources).slice(0, MAX_SOURCES_PER_DOMAIN);
  const findings = visible.map((source) => {
    const status = getAssessmentReportFindingStatusLabel(source.finding.status).toLocaleLowerCase('pt-BR');
    return `${source.snapshot.testLabel} (${status}): ${getSourceNarrative(source)}`;
  });
  const remaining = sources.length - visible.length;
  const suffix = remaining > 0
    ? `; além de ${remaining} ${remaining === 1 ? 'outro resultado' : 'outros resultados'} neste domínio`
    : '';
  return ensureSentence(`${domainLabel}: ${findings.join('; ')}${suffix}`);
};

const buildIntegratedAnalysis = (form: AssessmentReportEditorForm): string => {
  const sources = sortSources(form.sources);
  if (sources.length === 0) {
    return 'A análise integrada será sugerida após a seleção das avaliações-fonte.';
  }

  const byDomain = new Map<string, AssessmentReportSourceSnapshotV2[]>();
  sources.forEach((source) => {
    const domainLabel = getAssessmentReportDomainLabel(source.finding.domain);
    byDomain.set(domainLabel, [...(byDomain.get(domainLabel) ?? []), source]);
  });

  return [...byDomain.entries()]
    .map(([domainLabel, domainSources]) => buildDomainAnalysis(domainLabel, domainSources))
    .join(' ');
};

const buildPhysiotherapyConclusionScaffold = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string => {
  if (sources.length === 0) return ASSESSMENT_REPORT_CONCLUSION_COMPLETION_MARKER;

  const statusParts = buildStatusCountParts(sources);
  const factualOpening = statusParts.length > 0
    ? `As avaliações selecionadas registram ${joinNatural(statusParts)}.`
    : `Foram considerados ${formatEvaluationCount(sources.length)}.`;
  const highlighted = sortSources(sources)
    .filter((source) =>
      source.finding.status === 'altered'
      || source.finding.status === 'borderline')
    .slice(0, MAX_HIGHLIGHTED_SOURCES);
  const highlightedText = highlighted.length > 0
    ? ` Os principais pontos que requerem correlação clínica são: ${highlighted
        .map(formatSourceNarrative)
        .join('; ')}.`
    : '';

  return `${factualOpening}${highlightedText} ${ASSESSMENT_REPORT_CONCLUSION_COMPLETION_MARKER}`;
};

const buildTechnicalRecommendations = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string[] => {
  if (sources.length === 0) return [];

  const counts = countAssessmentReportFindings(sources);
  const recommendations = [
    'Correlacionar os resultados com a anamnese, o exame físico, os objetivos funcionais e a evolução clínica.',
  ];
  if (counts.altered > 0 || counts.borderline > 0) {
    recommendations.push(
      'Revisar os resultados alterados ou em faixa de atenção ao definir prioridades e condutas individualizadas.',
    );
  }
  if (counts.notClassified > 0 || counts.notInterpretable > 0) {
    recommendations.push(
      'Revisar validade, referências e condições de aplicação dos resultados sem conclusão classificatória.',
    );
  }
  recommendations.push(
    'Definir a necessidade e o intervalo de reavaliação conforme o plano de cuidado e a evolução observada.',
  );
  return recommendations;
};

const buildTechnicalLimitations = (
  sources: readonly AssessmentReportSourceSnapshotV2[],
): string | null => {
  if (sources.length === 0) return null;

  const counts = countAssessmentReportFindings(sources);
  const limitations = [
    'Esta análise está restrita aos instrumentos, às condições de aplicação e ao período selecionados; não substitui anamnese, exame físico ou julgamento clínico.',
  ];
  const validityLimited = sources.filter((source) => source.validity.status !== 'valid');
  if (validityLimited.length > 0) {
    limitations.push(
      `${formatEvaluationCount(validityLimited.length)} ${
        validityLimited.length === 1 ? 'possui' : 'possuem'
      } ressalva, invalidade ou validade não determinada.`,
    );
  }
  const unresolved = counts.notClassified + counts.notInterpretable;
  if (unresolved > 0) {
    limitations.push(
      `${formatResultCount(
        unresolved,
        'resultado não possui',
        'resultados não possuem',
      )} conclusão classificatória.`,
    );
  }
  return limitations.join(' ');
};

/**
 * Reconhece textos automáticos de versões anteriores para a apresentação e a edição.
 *
 * Esta função não seleciona fontes, não recalcula escores e não produz
 * diagnóstico, código CBDF ou código CIF. Não usar para novos documentos.
 */
export const buildLegacyAssessmentReportNarrativeSuggestions = (
  form: AssessmentReportEditorForm,
): AssessmentReportNarrativeSuggestions => ({
  purposeDescription: buildPurposeDescription(form),
  summary: {
    professionalOverview: buildProfessionalOverview(form),
    followUpGuidance: buildFollowUpGuidance(form.sources),
    observations: buildObservations(form.sources),
  },
  technical: {
    clinicalContext: buildTechnicalClinicalContext(form),
    integratedAnalysis: buildIntegratedAnalysis(form),
    physiotherapyConclusion: buildPhysiotherapyConclusionScaffold(form.sources),
    recommendations: buildTechnicalRecommendations(form.sources),
    limitations: buildTechnicalLimitations(form.sources),
  },
});

/** Campos clínicos pertencem ao profissional; os resultados já estão nas tabelas. */
export const buildAssessmentReportNarrativeSuggestions = (
  form: AssessmentReportEditorForm,
): AssessmentReportNarrativeSuggestions => {
  void form;
  return {
    purposeDescription: '',
    summary: { professionalOverview: '', followUpGuidance: null, observations: [] },
    technical: {
      clinicalContext: '',
      integratedAnalysis: '',
      physiotherapyConclusion: '',
      recommendations: [],
      limitations: null,
    },
  };
};

const sameString = (
  left: string | null | undefined,
  right: string | null | undefined,
): boolean => (left ?? '') === (right ?? '');

const sameStringList = (
  left: readonly string[] | null | undefined,
  right: readonly string[] | null | undefined,
): boolean => {
  const safeLeft = left ?? [];
  const safeRight = right ?? [];
  return safeLeft.length === safeRight.length
    && safeLeft.every((item, index) => item === safeRight[index]);
};

const isEmptyString = (value: string | null | undefined): boolean =>
  !value?.trim();

const isEmptyStringList = (value: readonly string[] | null | undefined): boolean =>
  !value || value.length === 0;

const splitNarrativeLines = (value: string | null | undefined): string[] =>
  (value ?? '')
    .split(/\r?\n/)
    .map((item) => item.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter(Boolean);

const reconcileString = (
  previousValue: string | null | undefined,
  currentValue: string | null | undefined,
  previousSuggestion: string | null,
  nextSuggestion: string | null,
  options: Required<ReconcileAssessmentReportSuggestionsOptions>,
): string | null => {
  if (options.force) return nextSuggestion;
  const wasEdited = !sameString(previousValue, currentValue);
  if (wasEdited) return currentValue ?? null;
  if (options.fillEmpty && isEmptyString(currentValue)) return nextSuggestion;
  if (sameString(currentValue, previousSuggestion)) return nextSuggestion;
  return currentValue ?? null;
};

const reconcileStringList = (
  previousValue: readonly string[] | null | undefined,
  currentValue: readonly string[] | null | undefined,
  previousSuggestion: readonly string[],
  nextSuggestion: readonly string[],
  options: Required<ReconcileAssessmentReportSuggestionsOptions>,
): string[] => {
  if (options.force) return [...nextSuggestion];
  const wasEdited = !sameStringList(previousValue, currentValue);
  if (wasEdited) return [...(currentValue ?? [])];
  if (options.fillEmpty && isEmptyStringList(currentValue)) return [...nextSuggestion];
  if (sameStringList(currentValue, previousSuggestion)) return [...nextSuggestion];
  return [...(currentValue ?? [])];
};

/**
 * Atualiza sugestões após uma mudança de fontes/contexto sem apagar conteúdo
 * autoral. `previous` representa o formulário antes da mudança e `next`, o
 * formulário já modificado (por exemplo, com uma nova fonte).
 */
export const reconcileAssessmentReportSuggestions = (
  previous: AssessmentReportEditorForm,
  next: AssessmentReportEditorForm,
  options: ReconcileAssessmentReportSuggestionsOptions = {},
): AssessmentReportEditorForm => {
  const resolvedOptions: Required<ReconcileAssessmentReportSuggestionsOptions> = {
    fillEmpty: options.fillEmpty ?? true,
    force: options.force ?? false,
  };
  const previousSuggestions = buildLegacyAssessmentReportNarrativeSuggestions(previous);
  const nextSuggestions = buildAssessmentReportNarrativeSuggestions(next);
  const purposeDescription = reconcileString(
    previous.purpose.description,
    next.purpose.description,
    previousSuggestions.purposeDescription,
    nextSuggestions.purposeDescription,
    resolvedOptions,
  ) ?? '';

  if (next.mode === 'assessment_summary') {
    const previousOverview =
      previous.mode === 'assessment_summary'
        ? previous.content.professionalOverview
        : previous.content.integratedAnalysis;
    const previousOverviewSuggestion =
      previous.mode === 'assessment_summary'
        ? previousSuggestions.summary.professionalOverview
        : previousSuggestions.technical.integratedAnalysis;
    const previousFollowUp =
      previous.mode === 'assessment_summary'
        ? previous.content.followUpGuidance
        : previous.content.recommendations.join('\n') || null;
    const previousFollowUpSuggestion =
      previous.mode === 'assessment_summary'
        ? previousSuggestions.summary.followUpGuidance
        : previousSuggestions.technical.recommendations.join('\n') || null;
    const previousObservations =
      previous.mode === 'assessment_summary' ? previous.content.observations : [];
    return {
      ...next,
      purpose: { ...next.purpose, description: purposeDescription },
      content: {
        ...next.content,
        professionalOverview:
          reconcileString(
            previousOverview,
            next.content.professionalOverview,
            previousOverviewSuggestion,
            nextSuggestions.summary.professionalOverview,
            resolvedOptions,
          ) ?? '',
        followUpGuidance: reconcileString(
          previousFollowUp,
          next.content.followUpGuidance,
          previousFollowUpSuggestion,
          nextSuggestions.summary.followUpGuidance,
          resolvedOptions,
        ),
        observations: reconcileStringList(
          previousObservations,
          next.content.observations,
          previous.mode === 'assessment_summary'
            ? previousSuggestions.summary.observations
            : [],
          nextSuggestions.summary.observations,
          resolvedOptions,
        ),
      },
    };
  }

  const previousIntegratedAnalysis =
    previous.mode === 'physiotherapy_technical_report'
      ? previous.content.integratedAnalysis
      : previous.content.professionalOverview;
  const previousIntegratedAnalysisSuggestion =
    previous.mode === 'physiotherapy_technical_report'
      ? previousSuggestions.technical.integratedAnalysis
      : previousSuggestions.summary.professionalOverview;
  const previousRecommendations =
    previous.mode === 'physiotherapy_technical_report'
      ? previous.content.recommendations
      : splitNarrativeLines(previous.content.followUpGuidance);
  const previousRecommendationsSuggestion =
    previous.mode === 'physiotherapy_technical_report'
      ? previousSuggestions.technical.recommendations
      : splitNarrativeLines(previousSuggestions.summary.followUpGuidance);
  return {
    ...next,
    purpose: { ...next.purpose, description: purposeDescription },
    content: {
      ...next.content,
      clinicalContext:
        reconcileString(
          previous.mode === 'physiotherapy_technical_report'
            ? previous.content.clinicalContext
            : '',
          next.content.clinicalContext,
          previous.mode === 'physiotherapy_technical_report'
            ? previousSuggestions.technical.clinicalContext
            : '',
          nextSuggestions.technical.clinicalContext,
          resolvedOptions,
        ) ?? '',
      integratedAnalysis:
        reconcileString(
          previousIntegratedAnalysis,
          next.content.integratedAnalysis,
          previousIntegratedAnalysisSuggestion,
          nextSuggestions.technical.integratedAnalysis,
          resolvedOptions,
        ) ?? '',
      physiotherapyConclusion:
        reconcileString(
          previous.mode === 'physiotherapy_technical_report'
            ? previous.content.physiotherapyConclusion
            : '',
          next.content.physiotherapyConclusion,
          previous.mode === 'physiotherapy_technical_report'
            ? previousSuggestions.technical.physiotherapyConclusion
            : '',
          nextSuggestions.technical.physiotherapyConclusion,
          resolvedOptions,
        ) ?? '',
      recommendations: reconcileStringList(
        previousRecommendations,
        next.content.recommendations,
        previousRecommendationsSuggestion,
        nextSuggestions.technical.recommendations,
        resolvedOptions,
      ),
      limitations: reconcileString(
        previous.mode === 'physiotherapy_technical_report'
          ? previous.content.limitations
          : null,
        next.content.limitations,
        previous.mode === 'physiotherapy_technical_report'
          ? previousSuggestions.technical.limitations
          : null,
        nextSuggestions.technical.limitations,
        resolvedOptions,
      ),
    },
  };
};

/**
 * Preenche apenas campos vazios por padrão. `force` representa uma restauração
 * explícita de todas as sugestões do modo atual.
 */
export const refreshAssessmentReportSuggestions = (
  form: AssessmentReportEditorForm,
  options: { force?: boolean } = {},
): AssessmentReportEditorForm =>
  reconcileAssessmentReportSuggestions(form, form, {
    fillEmpty: true,
    force: options.force ?? false,
  });
